/**
 * <strong>Purpose:</strong> <br>
 * Provides the ability to handle the behavior that is shared between input-type widgets(textfield, textarea).
 * <br />
 * It has methods to handle user interaction while typing, handle enter key and also handle out of focus(blur) activity
 * in an input-type widget(textfield, textarea).
 * <br><br>
 *
 * <strong>Contract:</strong> <br>
 * PlaceholderMixin, ChangeEventMixin, TypingEventMixin
 *
 * @module mw-form/mixins/property/SharedTextInputValueMixin
 *
 * @copyright Copyright 2014 The MathWorks, Inc.
 */

define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/keys",
    "dijit/_FocusMixin",

    "mw-form/mixins/property/PlaceholderMixin",
    "mw-form/mixins/event/ChangeEventMixin",
    "mw-form/mixins/event/TypingEventMixin",
    "mw-utils/ValidationFunctions"

], function (declare, lang, keys, _FocusMixin, PlaceholderMixin, ChangeEventMixin,
             TypingEventMixin, ValidationFunctions) {

    return declare([_FocusMixin, PlaceholderMixin, ChangeEventMixin, TypingEventMixin], {

        constructor: function(){

            /**
             * This object is used by widgets that require user input to prevent a "typing"
             * event from happening when a user is interacting with the widgets.
             *
             * Those not supported by dojo/keys are listed by their keyCode
             * see https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/keyCode
             */
            this._preventTypingEventForKeys = [
                keys.UP_ARROW,
                keys.DOWN_ARROW,
                keys.RIGHT_ARROW,
                keys.LEFT_ARROW,
                keys.PAGE_DOWN,
                keys.PAGE_UP,
                keys.HOME,
                keys.END,
                keys.ESCAPE,
                keys.CAPS_LOCK,
                16, // "ShiftLeft" and "ShiftRight"
                17, // "ControlLeft" and "ControlRight"
                18, // "AltLeft" and "AltRight"
                92  // "OSRight" (Windows, Linux)
                /* Keys 91 and 93  (OSLeft and OSRight on Mac) are not included in this list
                   due to keyup event not being triggered for the paste shortcut on a Mac.
                   See g1711199 for details. */
            ];

        },

        /**
         * Allows adding keys that will not trigger the 'typing' event
         * @param keys: Array of keys that will not trigger the 'typing' event
         */
        _addKeysThatPreventTypingEvent: function(keys){

            // Check that the input is an array
            if(!Array.isArray(keys)){
                throw "Input to _addKeysThatPreventTypingEvent was not an array";
            }

            // Append the keys to the existing list
            this._preventTypingEventForKeys = this._preventTypingEventForKeys.concat(keys);
        },

        postCreate: function () {

            this.inherited(arguments);
            this.on("keyup", this._onKeyUp.bind(this));
            this.on("drop", this._onDrop.bind(this));
        },

        /**
         * Uses a setTimeout to push handling the new text input to the back of the queue 
         * because the browser input value needs to react to the drop and be updated first
         * @param {DragEvent} e 
         */
        _onDrop: function (e) {
           setTimeout(function() {
                this._handleNewTextInput();
            }.bind(this), 0);
        },

        _onKeyUp: function (e) {
            // Don't fire typing event for the keys specified in _preventTypingEventForKeys array
            if (this._preventTypingEventForKeys.indexOf(e.keyCode) >= 0) {
                return;
            }

            this._handleNewTextInput();
        },

        /**
         * Emits a 'typing' event and updates the previous text input value
         */
        _handleNewTextInput: function () {
            var textInputValue = this.inputNode.value;
            this._triggerTypingEvent(textInputValue, this._previousTextInputValue);
            
            // Update the oldValue for the typing event. This is not redundant with updating it in _setValueAttr
            // because there can be many typing events followed by a single _setValueAttr e.g. due to ENTER or blur.
            this._previousTextInputValue = this.inputNode.value;
        },
        
        _setValueAttr: function () {
            this.inherited(arguments);
            if (this.inputNode !== undefined) { // sanity check in case node was just destroyed e.g. by dialog dismissal
                this._previousTextInputValue = this.inputNode.value;
            }
        },

        // overriding _onBlur from _FocusMixin
        _onBlur: function () {
            // when the text input goes out of focus a change event is fired
            var textInputValue,
                oldValue,
                newValue;

            textInputValue = this.inputNode.value;
            oldValue = this.get("value");

            var parent = this.getParent();
            if (parent != null && typeof parent.get("validationState") != "undefined"
                && parent.get("validationState") != null && parent.get("validationState") === "normal"
                && typeof parent.valuePrecision != "undefined" && parent.valuePrecision != null) {
                // ValidationFunctions.convertValueToNumber used to take care of Spinner infinte value
                // For non-editable spinner no change event should be triggered
                var convertedOldValue = (ValidationFunctions.isEmpty(oldValue)) ? oldValue : ValidationFunctions.convertValueToNumber(oldValue).toFixed(parent.valuePrecision);
                var convertedNewValue = (ValidationFunctions.isEmpty(textInputValue)) ? oldValue : ValidationFunctions.convertValueToNumber(textInputValue).toFixed(parent.valuePrecision);

                if (parent.get("editable") && convertedOldValue !== convertedNewValue) {
                    this._set("value", textInputValue);
                    this._previousTextInputValue = textInputValue;
                    newValue = this.get("value");
                    // do not emit the event if there is no change in values
                    if (oldValue !== newValue) {
                        this._triggerChangeEvent("value", oldValue, newValue);
                    }
                }
            } else {
                //g1491831
                //update the TextField Object value with out going through the regular
                //set as we dont need to set html input element again on _onBlur as the
                //input elemnt already have the same value, Going through the regular set
                //has the side effects of loosing the cursor point and selection range
                //when we do the programmatic set focus
                this._set("value", textInputValue);
                this._previousTextInputValue = textInputValue;
                newValue = this.get("value");
                // do not emit the event if there is no change in values
                if (oldValue !== newValue) {
                    this._triggerChangeEvent("value", oldValue, newValue);
                }
            }

            //g1437888
            //Call to the super method after commiting the value for the following reasons
            //As the onBlur is fired have the value commited so that other components depending
            //on the value based on the Blur event get the commited value
            //Also, dojo stock widgets commit the value before firing the Blur event
            this.inherited(arguments);

        }
    });

});
